/**
 * Shared "is the pointer over this chart's ECharts canvas" state, keyed by chart id.
 *
 * Written by useChartMouseInteractions on the canvas DOM mouseenter/mouseleave; read by
 * useChartTooltips. This lets the tooltip-rebuild path (useChartTooltips re-applying the
 * tooltip option after a `notMerge` rebuild in useChartOptions) keep `tooltip.show` off
 * while the pointer is away — otherwise the rebuild re-enables `show` and the tooltip
 * reappears stuck on chart→widget moves.
 *
 * Why `show:false` rather than HideTip / triggerOn:'none': those are defeated by ECharts'
 * `_keepShow` re-show after a re-render and by the enterable tooltip's `_inContent`
 * hide-guard (hd-echarts hideLater: `!(this._inContent && this._enterable)`). Removing the
 * tooltip component outright is the only reliable dismissal. (EVA-6690)
 *
 * Module-level (not React state) because it is read/written from pointer-frequency event
 * handlers and must not trigger re-renders. A stale id left after unmount is harmless — it
 * is simply never read again.
 */
const pointerOverChartById = new Map<string, boolean>();

/** Record whether the pointer is currently over the given chart's canvas. */
export function setPointerOverChart(id: string, isOver: boolean): void {
  pointerOverChartById.set(id, isOver);
}

/**
 * Whether the pointer is currently over the given chart's canvas. Defaults to `true`
 * (assume present) so a chart that has not been hovered yet keeps normal tooltip
 * behaviour — `show` is only forced off after an observed mouseleave.
 */
export function isPointerOverChart(id: string): boolean {
  return pointerOverChartById.get(id) ?? true;
}
